import React from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

export default function Q360EmptyState({
  message = "Aucune donnée disponible.",
  title,
  description,
  compact = false,
  icon = "∅",
  actionLabel,
  onAction,
  className,
}) {
  const hasStructuredCopy = Boolean(title || description);
  const hasAction = Boolean(actionLabel && onAction);

  return (
    <EmptyState
      role="status"
      aria-live="polite"
      $compact={compact}
      className={className}
      data-density={compact ? "compact" : "default"}
    >
      <EmptyIcon aria-hidden="true" $compact={compact}>
        {icon}
      </EmptyIcon>

      <EmptyCopy>
        {hasStructuredCopy ? (
          <>
            {title && <EmptyTitle>{title}</EmptyTitle>}

            {description && (
              <EmptyDescription>
                {description}
              </EmptyDescription>
            )}
          </>
        ) : (
          <EmptyMessage>{message}</EmptyMessage>
        )}
      </EmptyCopy>

      {hasAction && (
        <EmptyAction
          type="button"
          onClick={onAction}
        >
          {actionLabel}
        </EmptyAction>
      )}
    </EmptyState>
  );
}

Q360EmptyState.propTypes = {
  message: PropTypes.string,
  title: PropTypes.string,
  description: PropTypes.string,
  compact: PropTypes.bool,
  icon: PropTypes.node,
  actionLabel: PropTypes.string,
  onAction: PropTypes.func,
  className: PropTypes.string,
};

const EmptyState = styled.section`
  min-height: ${({ $compact }) => ($compact ? "52px" : "112px")};
  padding: ${({ $compact }) =>
    $compact ? "0.7rem 0.85rem" : "1.1rem"};
  border: 1px dashed var(
    --q360-border-subtle,
    rgba(255, 255, 255, 0.14)
  );
  border-radius: ${({ $compact }) =>
    $compact
      ? "var(--q360-radius-sm, 10px)"
      : "18px"};
  background: rgba(255, 255, 255, 0.035);
  color: var(--q360-text-primary, #f7f7f7);

  display: grid;
  grid-template-columns: ${({ $compact }) =>
    $compact ? "auto minmax(0, 1fr) auto" : "1fr"};
  align-items: center;
  justify-items: ${({ $compact }) =>
    $compact ? "stretch" : "center"};
  gap: ${({ $compact }) => ($compact ? "0.7rem" : "0.85rem")};

  text-align: ${({ $compact }) =>
    $compact ? "left" : "center"};
`;

const EmptyIcon = styled.span`
  width: ${({ $compact }) => ($compact ? "32px" : "42px")};
  height: ${({ $compact }) => ($compact ? "32px" : "42px")};
  border-radius: 999px;
  background: rgba(255, 249, 234, 0.1);
  color: var(--q360-text-primary, #fff9ea);

  display: inline-flex;
  align-items: center;
  justify-content: center;

  font-size: ${({ $compact }) =>
    $compact ? "0.95rem" : "1.15rem"};
  font-weight: 900;
`;

const EmptyCopy = styled.div`
  min-width: 0;
`;

const EmptyMessage = styled.p`
  margin: 0;
  color: var(
    --q360-text-muted,
    rgba(247, 247, 247, 0.76)
  );
  font-size: 0.9rem;
  font-weight: 750;
  line-height: 1.45;
`;

const EmptyTitle = styled.h3`
  margin: 0;
  color: var(--q360-text-primary, #f7f7f7);
  font-size: 1rem;
  font-weight: 850;
`;

const EmptyDescription = styled.p`
  margin: 0.35rem 0 0;
  color: var(
    --q360-text-muted,
    rgba(247, 247, 247, 0.7)
  );
  font-size: 0.88rem;
  line-height: 1.45;
`;

const EmptyAction = styled.button`
  min-height: 36px;
  padding: 0.5rem 0.85rem;
  border: none;
  border-radius: 999px;
  background: var(--q360-text-primary, #fff9ea);
  color: var(--q360-surface-base, #0b1328);
  font: inherit;
  font-size: 0.84rem;
  font-weight: 850;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    background: #ffffff;
  }

  &:focus-visible {
    outline: 2px solid #ffffff;
    outline-offset: 2px;
  }
`;
